import { Bell, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import { useDeleteNotice, useNoticesAdmin } from '@/api/queries/notices-queries'
import { useOrganization } from '@/api/queries/organization-queries'
import { AdminCardPage, CardListItem, SearchInput } from '@/components/commons'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type { Notice } from '@/types/notice'
import { getApiErrorMessage } from '@/utils/api-error'

/* ────────────────────────── Main Page ────────────────────────────── */

export const NotificationsPage = () => {
	const navigate = useNavigate()
	const { t } = useTranslation()

	// ── Data ────────────────────────────────────────────────────────
	const { data: noticesData, isLoading } = useNoticesAdmin()
	const { data: orgData } = useOrganization()

	const notices = noticesData?.items ?? []
	const branches = orgData ?? []

	// ── Mutations ───────────────────────────────────────────────────
	const deleteMutation = useDeleteNotice()

	// ── Local UI state ──────────────────────────────────────────────
	const [search, setSearch] = useState('')
	const [deletingItem, setDeletingItem] = useState<Notice | null>(null)

	// ── Helpers ─────────────────────────────────────────────────────
	const getBranchName = (branchId: string) =>
		branches.find((b) => b.id === branchId)?.name ?? branchId

	const filtered = notices.filter((notif) => {
		const q = search.toLowerCase()
		const branchNames = notif.branchIds.map((id) => getBranchName(id)).join(' ')
		return (
			notif.title.toLowerCase().includes(q) ||
			notif.message.toLowerCase().includes(q) ||
			branchNames.toLowerCase().includes(q)
		)
	})

	// ── CRUD handlers ────────────────────────────────────────────────
	const handleOpenCreate = () =>
		navigate(ROUTES.ADMINISTRATION.NOTIFICATIONS_FORM)

	const handleOpenEdit = (notif: Notice) =>
		navigate(`${ROUTES.ADMINISTRATION.NOTIFICATIONS_FORM}?id=${notif.id}`)

	const handleOpenDelete = (notification: Notice) => {
		setDeletingItem(notification)
	}

	const handleConfirmDelete = async () => {
		if (!deletingItem) return
		try {
			await deleteMutation.mutateAsync(deletingItem.id)
			toast.success(
				t('administration.notifications.deleteSuccess', {
					title: deletingItem.title,
				}),
			)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
		setDeletingItem(null)
	}

	return (
		<>
			<AdminCardPage
				icon={<Bell className="size-5" />}
				title={t('administration.notifications.title')}
				subtitle={t('administration.notifications.subtitle')}
				onBack={() =>
					navigate(adminBackRoute(ROUTES.ADMINISTRATION.NOTIFICATIONS))
				}
				footerText={`${filtered.length} ${
					filtered.length === 1
						? t('administration.notifications.countSingular')
						: t('administration.notifications.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.notifications.createNew')}</span>
					</Button>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.notifications.searchPlaceholder')}
				/>

				{isLoading ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<Bell className="size-8 text-kaefer-light-silver animate-pulse" />
						<p className="text-sm">{t('common.loading')}</p>
					</div>
				) : filtered.length === 0 ? (
					<div className="flex flex-col items-center gap-2 py-16 text-center text-muted-foreground">
						<Bell className="size-8 text-kaefer-light-silver" />
						<p className="text-sm">
							{t('administration.notifications.noResults')}
						</p>
					</div>
				) : (
					<div className="space-y-2">
						{filtered.map((notification) => {
							const badge = notification.isGlobal
								? t('administration.notifications.global')
								: notification.branchIds
										.map((id) => getBranchName(id))
										.join(', ') || '—'
							return (
								<CardListItem
									key={notification.id}
									badge={badge}
									title={notification.title}
									subtitle={notification.message}
									details={[
										{
											label: t(
												'administration.notificationsForm.priorityLabel',
											),
											value: t(
												`administration.notificationsForm.priority_${notification.priority}`,
											),
										},
										{
											label: t('administration.notificationsForm.typeLabel'),
											value: t(
												`administration.notificationsForm.type_${notification.type}`,
											),
										},
										{
											label: t('administration.notifications.rolesLabel'),
											value:
												notification.roles.length === 0
													? t('administration.notifications.allRoles')
													: notification.roles
															.map((role) => t(`roles.${role}`))
															.join(', '),
										},
										{
											label: t('administration.notifications.statusLabel'),
											value: t(
												`administration.notifications.status_${notification.status}`,
											),
										},
									]}
									onEdit={() => handleOpenEdit(notification)}
									onDelete={() => handleOpenDelete(notification)}
								/>
							)
						})}
					</div>
				)}
			</AdminCardPage>
			<AlertDialog
				open={!!deletingItem}
				onOpenChange={() => setDeletingItem(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('administration.notifications.deleteConfirmTitle')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('administration.notifications.deleteConfirmMessage', {
								title: deletingItem?.title,
							})}
							{(deletingItem?.media.length ?? 0) > 0 &&
								` ${t('administration.notifications.deleteConfirmMedia')}`}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDelete}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
